// src/pages/produtos/produtos/StatusProdutoDelivery.js
import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useProduto } from '../../../contexts/produtos/ProdutoContext';
import { FaSortUp, FaSortDown } from 'react-icons/fa';
import { FiClock, FiPackage, FiX } from 'react-icons/fi';
import ModalHorariosProduto from './ModalHorariosProduto';
import ModalContainer from '../../../components/modalForm/ModalContainer';
import ModalHeader from '../../../components/modalForm/ModalHeader';
import ModalBody from '../../../components/modalForm/ModalBody';
import ModalTable from '../../../components/modalForm/ModalTable';
import ModalFooter from '../../../components/modalForm/ModalFooter';
import Input from '../../../components/input/Input';
import InputCheck from '../../../components/input/InputCheck';
import LazyLoading from '../../../components/modalForm/LazyLoading';
import './StatusProdutoDelivery.css';

// Função para remover acentos
const removerAcentos = (str) => String(str ?? '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLocaleLowerCase('pt-BR');

const StatusProdutoDelivery = ({ show, onHide, onUpdateStatus }) => {
  const { produtos, alteraVendeApp, alteraEsgotado, alteraInativo } = useProduto();
  const [produtoFilter, setProdutoFilter] = useState('');
  const [grupoFilter, setGrupoFilter] = useState('');
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [selectedGroupIndex, setSelectedGroupIndex] = useState(0);
  const [sortField, setSortField] = useState('id');
  const [sortDirection, setSortDirection] = useState('asc');
  const [listarInativos, setListarInativos] = useState(false);
  const [showHorariosModal, setShowHorariosModal] = useState(false);
  const [produtoHorarios, setProdutoHorarios] = useState(null);
  const [pendentes, setPendentes] = useState(new Set());
  const pendentesRef = useRef(new Set());
  const produtoInputRef = useRef(null);
  const groupTableRef = useRef(null);

  useEffect(() => {
    if (show) {
      setProdutoFilter('');
      setGrupoFilter('');
      setSelectedGroup(null);
      setSelectedGroupIndex(0);
      setListarInativos(false);
      setShowHorariosModal(false);
      setProdutoHorarios(null);
      produtoInputRef.current?.focus();
    }
  }, [show]);

  // Obter grupos
  const grupos = useMemo(() => {
    const gruposMap = new Map();
    produtos.forEach(produto => {
      if (produto.grupo && (listarInativos || !produto.grupo.inativo)) gruposMap.set(produto.grupo.id, produto.grupo);
    });
    return Array.from(gruposMap.values())
      .filter(grupo => removerAcentos(grupo.descricao).includes(removerAcentos(grupoFilter)))
      .sort((a, b) => removerAcentos(a.descricao).localeCompare(removerAcentos(b.descricao), 'pt-BR'));
  }, [produtos, listarInativos, grupoFilter]);

  useEffect(() => {
    const index = selectedGroup ? grupos.findIndex(grupo => grupo.id === selectedGroup.id) : -1;
    if (selectedGroup && index < 0) setSelectedGroup(null);
    setSelectedGroupIndex(index + 1);
  }, [grupos, selectedGroup]);

  // Efeito para manter grupo visível
  useEffect(() => {
    groupTableRef.current?.querySelector('button[aria-pressed="true"]')?.scrollIntoView?.({ block: 'nearest' });
  }, [selectedGroupIndex]);

  // Filtrar produtos
  const sortedProdutos = useMemo(() => {
    const filteredProdutos = produtos.filter(produto =>
      removerAcentos(produto.descricao).includes(removerAcentos(produtoFilter)) &&
      (listarInativos || (!produto.grupo?.inativo && !produto.inativo)) &&
      (!selectedGroup || produto.grupo?.id === selectedGroup.id)
    );
    // Ordenar
    return filteredProdutos.sort((a, b) => {
      const resultado = sortField === 'id'
        ? Number(a.id) - Number(b.id)
        : removerAcentos(a.descricao).localeCompare(removerAcentos(b.descricao), 'pt-BR');
      return sortDirection === 'asc' ? resultado : -resultado;
    });
  }, [produtos, produtoFilter, listarInativos, selectedGroup, sortField, sortDirection]);

  const handleSort = (field) => {
    setSortDirection(sortField === field && sortDirection === 'asc' ? 'desc' : 'asc');
    setSortField(field);
  };

  // Navegação grupos
  const handleKeyDown = (event) => {
    if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') return;
    event.preventDefault();
    const total = grupos.length + 1;
    const index = (selectedGroupIndex + (event.key === 'ArrowDown' ? 1 : -1) + total) % total;
    setSelectedGroupIndex(index);
    setSelectedGroup(index === 0 ? null : grupos[index - 1]);
    groupTableRef.current?.querySelectorAll('tbody button')[index]?.focus();
  };

  const atualizarStatus = async (produto, campo, value, acao) => {
    if (pendentesRef.current.has(produto.id)) return;
    pendentesRef.current.add(produto.id);
    setPendentes(new Set(pendentesRef.current));
    try {
      const resultado = await acao(produto.id, value);
      if (resultado !== false) onUpdateStatus?.({ ...produto, [campo]: value });
    } finally {
      pendentesRef.current.delete(produto.id);
      setPendentes(new Set(pendentesRef.current));
    }
  };

  const handleCheckVendeApp = (produto, value) => atualizarStatus(produto, 'vendeApp', value, alteraVendeApp);
  const handleCheckEsgotado = (produto, value) => atualizarStatus(produto, 'esgotado', value, alteraEsgotado);
  const handleCheckInativo = (produto, value) => atualizarStatus(produto, 'inativo', value, alteraInativo);
  const abrirModalHorarios = (produto) => { setProdutoHorarios(produto); setShowHorariosModal(true); };
  const fecharModalHorarios = () => { setShowHorariosModal(false); setProdutoHorarios(null); };

  const limparFiltros = () => {
    setGrupoFilter('');
    setProdutoFilter('');
    setSelectedGroup(null);
    produtoInputRef.current?.focus();
  };

  const renderOrdenador = (campo, titulo) => (
    <button type="button" className="status-sort" onClick={() => handleSort(campo)}>
      {titulo}{sortField === campo && (sortDirection === 'asc' ? <FaSortUp aria-hidden="true" /> : <FaSortDown aria-hidden="true" />)}
    </button>
  );

  return (
    <>
      <ModalContainer show={show} onHide={onHide} largura="1100px" variant="busca" contentClassName="status-produto-delivery">
        <ModalHeader title="Status dos produtos no delivery" subtitle="Controle a disponibilidade e os horários dos produtos." icon={FiPackage} onHide={onHide} />
        <ModalBody>
          <div className="status-filtros">
            <div className="status-filtro-grupo"><Input label="Grupo" placeholder="Filtrar grupos" value={grupoFilter} onChange={e => setGrupoFilter(e.target.value)} size="100%" /></div>
            <div className="status-filtro-produto"><Input label="Produto" placeholder="Filtrar pela descrição" value={produtoFilter} onChange={e => setProdutoFilter(e.target.value)} inputRef={produtoInputRef} size="100%" autoFocus /></div>
            <InputCheck label="Mostrar inativos" checked={listarInativos} onChange={e => setListarInativos(e.target.checked)} />
            <button type="button" className="btn btn-outline-secondary" onClick={limparFiltros}><FiX aria-hidden="true" /> Limpar</button>
          </div>
          <div className="status-listas">
            <section aria-label="Filtro de grupos" className="status-grupos">
              <ModalTable aria-label="Grupos" containerRef={groupTableRef} onKeyDown={handleKeyDown}>
                <thead><tr><th scope="col">Grupos <span>{grupos.length}</span></th></tr></thead>
                <tbody>
                  <tr><td><button type="button" aria-pressed={!selectedGroup} onClick={() => setSelectedGroup(null)}>Todos os grupos</button></td></tr>
                  {grupos.map(grupo => <tr key={grupo.id}><td><button type="button" aria-pressed={selectedGroup?.id === grupo.id} onClick={() => setSelectedGroup(grupo)}>{grupo.descricao}</button></td></tr>)}
                </tbody>
              </ModalTable>
            </section>
            <section aria-label="Produtos e disponibilidade" className="status-produtos">
              <div className="status-resultado"><strong>{selectedGroup?.descricao || 'Todos os produtos'}</strong><span>{sortedProdutos.length} {sortedProdutos.length === 1 ? 'produto' : 'produtos'}</span></div>
              <LazyLoading items={sortedProdutos} batchSize={100} resetKey={`${grupoFilter}|${produtoFilter}|${selectedGroup?.id || ''}|${listarInativos}|${sortField}|${sortDirection}`}>
                {({ visibleItems, containerRef, handleScroll }) => (
                  <ModalTable aria-label="Status dos produtos" containerRef={containerRef} onContainerScroll={handleScroll} emptyMessage="Nenhum produto encontrado para estes filtros.">
                    <thead><tr>
                      <th scope="col" className="status-id" aria-sort={sortField === 'id' ? (sortDirection === 'asc' ? 'ascending' : 'descending') : 'none'}>{renderOrdenador('id', 'ID')}</th>
                      <th scope="col" className="status-descricao" aria-sort={sortField === 'descricao' ? (sortDirection === 'asc' ? 'ascending' : 'descending') : 'none'}>{renderOrdenador('descricao', 'Descrição')}</th>
                      <th scope="col" className="status-horarios">Horários</th>
                      <th scope="col" className="status-flag">Inativo</th>
                      <th scope="col" className="status-flag">Vende no app</th>
                      <th scope="col" className="status-flag">Esgotado</th>
                    </tr></thead>
                    <tbody>{visibleItems.map(produto => (
                      <tr key={produto.id} aria-busy={pendentes.has(produto.id)}>
                        <td className="status-id">{produto.id}</td>
                        <td className="status-descricao">{produto.descricao}</td>
                        <td className="status-horarios"><button type="button" className="btn btn-outline-primary btn-sm" aria-label={`Horários de ${produto.descricao}`} disabled={pendentes.has(produto.id)} onClick={() => abrirModalHorarios(produto)}><FiClock aria-hidden="true" /><span>Horários</span></button></td>
                        <td className="status-flag"><InputCheck label={<span className="visually-hidden">Inativo: {produto.descricao}</span>} checked={!!produto.inativo} disabled={pendentes.has(produto.id)} onChange={e => handleCheckInativo(produto, e.target.checked)} /></td>
                        <td className="status-flag"><InputCheck label={<span className="visually-hidden">Vende no app: {produto.descricao}</span>} checked={!!produto.vendeApp} disabled={pendentes.has(produto.id)} onChange={e => handleCheckVendeApp(produto, e.target.checked)} /></td>
                        <td className="status-flag"><InputCheck label={<span className="visually-hidden">Esgotado: {produto.descricao}</span>} checked={!!produto.esgotado} disabled={pendentes.has(produto.id)} onChange={e => handleCheckEsgotado(produto, e.target.checked)} /></td>
                      </tr>
                    ))}</tbody>
                  </ModalTable>
                )}
              </LazyLoading>
            </section>
          </div>
        </ModalBody>
        <ModalFooter><span className="status-ajuda">As alterações de status são salvas automaticamente.</span><button type="button" className="btn btn-secondary" onClick={onHide}>Fechar</button></ModalFooter>
      </ModalContainer>
      <ModalHorariosProduto show={show && showHorariosModal} onHide={fecharModalHorarios} produto={produtoHorarios} />
    </>
  );
};

export default StatusProdutoDelivery;
